import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../api';
import './share-views-modal.scss';

interface ViewInfo {
  slug: string;
  name: string;
}

interface ExistingShareToken {
  shareToken: string;
  viewSlugs: string[];
  createdAt: string;
}

interface ShareViewsModalProps {
  show: boolean;
  onClose: () => void;
  appId: string;
  views: ViewInfo[];
  activeViewSlug?: string;
}

const ShareViewsModal: React.FC<ShareViewsModalProps> = ({
  show,
  onClose,
  appId,
  views,
  activeViewSlug,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [selectedSlugs, setSelectedSlugs] = useState<Set<string>>(new Set());
  const [existingTokens, setExistingTokens] = useState<ExistingShareToken[]>(
    [],
  );
  const [newShareUrl, setNewShareUrl] = useState<string | null>(null);
  const [copying, setCopying] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);

  // Close on click outside
  useEffect(() => {
    if (!show) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [show, onClose]);

  // Pre-check the active view on open
  useEffect(() => {
    if (show) {
      setSelectedSlugs(
        new Set(
          activeViewSlug ? [activeViewSlug] : [views[0]?.slug].filter(Boolean),
        ),
      );
      setNewShareUrl(null);
      fetchExistingTokens();
    }
  }, [show]);

  const fetchExistingTokens = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get(`/project-apps/${appId}/share-views`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.success) {
        setExistingTokens(res.data.tokens || []);
      }
    } catch (err) {
      console.error('Error fetching share tokens:', err);
    } finally {
      setLoading(false);
    }
  }, [appId, getAccessTokenSilently]);

  const handleToggleSlug = useCallback((slug: string) => {
    setSelectedSlugs((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) {
        next.delete(slug);
      } else {
        next.add(slug);
      }
      return next;
    });
  }, []);

  const handleCreate = useCallback(async () => {
    if (selectedSlugs.size === 0) return;
    setCreating(true);
    try {
      const token = await getAccessTokenSilently();
      const res = await api.post(
        `/project-apps/${appId}/share-views`,
        { viewSlugs: Array.from(selectedSlugs) },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data?.success) {
        const url = `${window.location.origin}/shared-view/${res.data.shareToken}`;
        setNewShareUrl(url);
        await fetchExistingTokens();
      }
    } catch (err) {
      console.error('Error creating share link:', err);
    } finally {
      setCreating(false);
    }
  }, [appId, selectedSlugs, getAccessTokenSilently, fetchExistingTokens]);

  const handleRevoke = useCallback(
    async (shareToken: string) => {
      try {
        const token = await getAccessTokenSilently();
        await api.delete(`/project-apps/share-views/${shareToken}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setExistingTokens((prev) =>
          prev.filter((t) => t.shareToken !== shareToken),
        );
      } catch (err) {
        console.error('Error revoking share token:', err);
      }
    },
    [getAccessTokenSilently],
  );

  const handleCopy = useCallback(async (url: string, id: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopying(id);
      setTimeout(() => setCopying(null), 2000);
    } catch (err) {
      console.error('Error copying:', err);
    }
  }, []);

  if (!show) return null;

  return (
    <div className='share-views-dropdown' ref={dropdownRef}>
      <p className='share-views-dropdown__title'>Share this app</p>
      <p className='share-views-dropdown__desc'>
        Create a public link anyone can use to view and interact with the
        selected pages.
      </p>
      <p className='share-views-dropdown__label'>Include:</p>
      <div className='share-views-dropdown__checkboxes'>
        {views.map((v) => (
          <label key={v.slug} className='share-views-dropdown__checkbox'>
            <input
              type='checkbox'
              checked={selectedSlugs.has(v.slug)}
              onChange={() => handleToggleSlug(v.slug)}
            />
            <span>{v.name}</span>
          </label>
        ))}
      </div>

      <button
        className='share-views-dropdown__create-btn'
        onClick={handleCreate}
        disabled={selectedSlugs.size === 0 || creating}
      >
        {creating ? 'Creating...' : 'Create Link'}
      </button>

      {newShareUrl && (
        <div className='share-views-dropdown__new-url'>
          <input type='text' value={newShareUrl} readOnly />
          <button
            className='share-views-dropdown__copy-btn'
            onClick={() => handleCopy(newShareUrl, 'new')}
          >
            {copying === 'new' ? 'Copied!' : 'Copy'}
          </button>
        </div>
      )}

      {existingTokens.length > 0 && (
        <div className='share-views-dropdown__existing'>
          <p className='share-views-dropdown__label'>Active share links:</p>
          {existingTokens.map((t) => {
            const url = `${window.location.origin}/shared-view/${t.shareToken}`;
            return (
              <div
                key={t.shareToken}
                className='share-views-dropdown__token-row'
              >
                <div className='share-views-dropdown__token-info'>
                  <span className='share-views-dropdown__token-views'>
                    {(t.viewSlugs as string[]).join(', ')}
                  </span>
                  <span className='share-views-dropdown__token-date'>
                    {new Date(t.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <div className='share-views-dropdown__token-actions'>
                  <button
                    className='share-views-dropdown__copy-btn'
                    onClick={() => handleCopy(url, t.shareToken)}
                  >
                    {copying === t.shareToken ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    className='share-views-dropdown__revoke-btn'
                    onClick={() => handleRevoke(t.shareToken)}
                  >
                    Revoke
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {loading && existingTokens.length === 0 && (
        <p className='share-views-dropdown__loading'>Loading...</p>
      )}
    </div>
  );
};

export default ShareViewsModal;
